/**
 * `AddressManager` orchestrates address capture and postcode lookup.
 *
 * Responsibilities:
 * - Owns address UI state via `useReducer(AddressManagerReducer, defaultState)`.
 * - Coordinates postcode-based lookup calls through `AddressManagerApi`.
 * - Populates the country dropdown options (translated) on first render.
 * - Validates the current address when `validate` is toggled, returning the
 *   overall validity via `onAddressValidated` and field messages via
 *   `addressErrors`.
 * - Emits the current address to the parent when `update` is toggled.
 *
 * Key behaviour:
 * - If any part of the initial address is populated, the manual address form is
 *   shown and postcode lookup is hidden.
 * - Postcode lookup is only performed when the search postcode is valid and
 *   differs from `lastSearchedPostCode`.
 * - Address list options are truncated for display (see `truncate`).
 */
import React, { useReducer, useEffect } from 'react';
import {
  AddressDetails,
  AddressInfo,
  AddressOption,
  Country,
  SelectOpt
} from '../../common/CommonTypes';
import FindAddress from './FindAddress';
import { AddressManagerApi } from '../../../api/AddressManagerApi';
import AddressManagerReducer, {
  AddressSearchState
} from './AddressManagerReducer';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { SelectInputHelper } from '../SelectInput/SelectInput';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app-state/Hooks';

export type AddressManagerProps = {
  findAddressTitle: string;
  initialAddress: AddressDetails;
  update: boolean;
  validate: boolean;
  token: string;
  findAddressLabel?: string;
  maxPostCodeLimit?: number;
  allowAlphabetInPostCode?: boolean;
  isMandatoryAddressline2?: boolean;
  onAddressUpdate: (address: AddressDetails) => void;
  onAddressValidated: (isValid: boolean) => void;
  allowManualEntry: boolean;
};

const AddressManager = ({
  findAddressTitle,
  initialAddress,
  update,
  validate,
  token,
  findAddressLabel,
  maxPostCodeLimit,
  allowAlphabetInPostCode = true,
  isMandatoryAddressline2,
  onAddressUpdate,
  onAddressValidated,
  allowManualEntry
}: AddressManagerProps): JSX.Element => {
  const defaultState: AddressSearchState = {
    showAddressList: false,
    showAddressForm:
      initialAddress && initialAddress.addressLine1 ? true : false,
    addressList: [],
    address: initialAddress,
    selectedAddress: '',
    addressErrors: {},
    finding: false,
    searchPostCode: '',
    searchPostCodeError: '',
    lastSearchedPostCode: '',
    countries: [],
    showPostcodeLookUp: true
  };

  const [state, dispatch] = useReducer(AddressManagerReducer, defaultState);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const {
    showAddressList,
    showAddressForm,
    addressList,
    address,
    selectedAddress,
    addressErrors,
    finding,
    searchPostCode,
    searchPostCodeError,
    lastSearchedPostCode,
    countries,
    showPostcodeLookUp
  } = state;

  const { t } = useTranslation();

  const errorEmptyAddress1 = t('personal-details.error-empty-address1');
  const errorEmptyAddress2 = t('personal-details.error-empty-address2');
  const errorEmptyCountry = t('personal-details.error-empty-country');
  const errorEmptyPostcode = t('personal-details.error-empty-postcode');
  const errorEmptyCity = t('personal-details.error-empty-city');
  const errorEmptySearchPostcode = t(
    'personal-details.error-empty-search-postcode'
  );
  const errorPostcodeNotFound = t('personal-details.error-postcode-not-found');

  const setAddressErrors = (errors: AddressDetails) => {
    dispatch({
      type: 'set-address-errors',
      errors: errors
    });
  };

  const setAddress = (addressDetails: AddressDetails) => {
    dispatch({
      type: 'set-address',
      address: addressDetails
    });
  };

  const onChangeDetail = (
    event: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    const newAddress = {
      ...address,
      [key]: event.currentTarget.value ?? ''
    };

    setAddress(newAddress);
  };

  useEffect(() => {
    if (validate === true) {
      if (!showAddressForm) {
        setAddressFormVisible();
      }

      const isValid = validateAddress(address);
      onAddressValidated(isValid);
    }
  }, [validate]);

  useEffect(() => {
    if (update === true) {
      onAddressUpdate(address);
    }
  }, [update]);

  useEffect(() => {
    if (!countries || countries.length < 1) {
      populateCountries();
    }
    if (someAddressIsSet() === true) {
      setAddressFormVisible();
      setPostcodeLookup(false);
    } else {
      setPostcodeLookup(true);
    }
  }, []);

  const someAddressIsSet = (): boolean => {
    if (!address) return false;

    if (
      CommonValidation.isNotEmpty(address.addressLine1) ||
      CommonValidation.isNotEmpty(address?.addressLine2) ||
      CommonValidation.isNotEmpty(address?.townOrCity) ||
      CommonValidation.isNotEmpty(address?.region) ||
      CommonValidation.isNotEmpty(address?.postCode) ||
      CommonValidation.isNotEmpty(address?.country)
    ) {
      return true;
    }

    return false;
  };

  const populateCountries = async () => {
    const countryList: Country[] = [
      {
        iso3code: 'GBR',
        countryName: t('countries.united-kingdom'),
        alpha2code: 'GB'
      },
      {
        iso3code: 'DEU',
        countryName: t('countries.germany'),
        alpha2code: 'DE'
      },
      {
        iso3code: 'AUT',
        countryName: t('countries.austria'),
        alpha2code: 'AT'
      },
      {
        iso3code: 'ESP',
        countryName: t('countries.spain'),
        alpha2code: 'ES'
      }
    ];
    const countryOptions = getCountryOptions(countryList);

    dispatch({
      type: 'set-countries',
      countries: countryOptions
    });
  };

  const getCountryOptions = (lookupItems: Country[]) => {
    const items: SelectOpt[] = [];

    if (lookupItems === undefined || lookupItems === null) return items;

    lookupItems.sort((a, b) => {
      const nameA = a.countryName.toUpperCase();
      const nameB = b.countryName.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    lookupItems.forEach((item) => {
      const lookupOption = {
        id: item.alpha2code,
        text: item.countryName,
        value: item.iso3code,
        disabled: false
      };
      items.push(lookupOption);
    });

    return items;
  };

  const onCountryChange = (country: string) => {
    const newAddress = {
      ...address,
      ['country']: country ?? SelectInputHelper.NullValue()
    };

    setAddress(newAddress);
  };

  const onSelectAddress = (selectedAddressMoniker: string) => {
    const controller = new AbortController();
    setAddressErrors({});

    dispatch({
      type: 'set-selected-address',
      moniker: selectedAddressMoniker
    });

    if (SelectInputHelper.isNullValue(selectedAddressMoniker)) {
      //clicing Select... in the address list will clear the address textbox values
      setAddress({});
      setAddressFormVisible();
    } else {
      new AddressManagerApi().loadAddress(
        selectedAddressMoniker,
        token,
        setAddressSuccess,
        setAddressFailed,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  };

  const setAddressSuccess = (foundAddress: AddressInfo) => {
    if (foundAddress) {
      const country = countries.find(
        (c) => c.text.toLowerCase() === foundAddress.country.toLowerCase()
      );

      const addressDetail = {
        addressLine1: foundAddress.addressLine1,
        addressLine2: foundAddress.addressLine2,
        townOrCity: foundAddress.townOrCity,
        region: foundAddress.region,
        country: country?.value,
        postCode: foundAddress.postcode
      };

      setAddress(addressDetail);
      setAddressFormVisible();
    } else {
      setAddressFailedRoutine();
    }
  };

  const setAddressFailed = () => {
    setAddressFailedRoutine();
  };

  const setAddressFailedRoutine = () => {
    setAddress({});
    setAddressFormVisible();
  };

  const setAddressFormVisible = () => {
    dispatch({
      type: 'set-show-address-form',
      show: true
    });
  };

  const validateAddress = (addressToValidate: AddressDetails): boolean => {
    let isValid = true;
    let errors: AddressDetails = {};

    const addressLine1CharLimit = 100;
    const addressLine2CharLimit = 100;
    const postCodeCharLimit = maxPostCodeLimit ?? 10;
    const townOrCityCharLimit = 50;
    const countyCharLimit = 50;

    if (CommonValidation.isEmpty(addressToValidate?.addressLine1)) {
      errors = {
        ...errors,
        addressLine1: errorEmptyAddress1
      };
      isValid = false;
    } else {
      if (
        !CommonValidation.isValidLength(
          addressToValidate?.addressLine1 ?? '',
          addressLine1CharLimit
        )
      ) {
        errors = {
          ...errors,
          addressLine1: t(
            'personal-details.addressline1-error-exceeds-length',
            {
              addressLine1CharLimit: addressLine1CharLimit
            }
          )
        };
        isValid = false;
      }
    }
    if (isMandatoryAddressline2) {
      if (CommonValidation.isEmpty(addressToValidate?.addressLine2)) {
        errors = {
          ...errors,
          addressLine2: errorEmptyAddress2
        };
        isValid = false;
      }
    } else {
      if (
        !CommonValidation.isEmpty(addressToValidate?.addressLine2) &&
        !CommonValidation.isValidLength(
          addressToValidate?.addressLine2 ?? '',
          addressLine2CharLimit
        )
      ) {
        errors = {
          ...errors,
          addressLine2: t(
            'personal-details.addressline2-error-exceeds-length',
            {
              addressLine2CharLimit: addressLine2CharLimit
            }
          )
        };
        isValid = false;
      }

      if (
        CommonValidation.isEmpty(addressToValidate?.country) ||
        addressToValidate?.country === SelectInputHelper.NullValue()
      ) {
        errors = {
          ...errors,
          country: errorEmptyCountry
        };
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(addressToValidate?.postCode)) {
      errors = {
        ...errors,
        postCode: errorEmptyPostcode
      };
      isValid = false;
    }
    if (
      !allowAlphabetInPostCode &&
      !CommonValidation.isValidNumeric(addressToValidate?.postCode ?? '')
    ) {
      errors = {
        ...errors,
        postCode: t('personal-details.postcode-invalid-alphabet-error')
      };
      isValid = false;
    } else {
      if (
        !CommonValidation.isValidLength(
          addressToValidate?.postCode ?? '',
          postCodeCharLimit
        )
      ) {
        errors = {
          ...errors,
          postCode: t('personal-details.postcode-error-exceeds-length', {
            postCodeCharLimit: postCodeCharLimit
          })
        };
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(addressToValidate?.townOrCity)) {
      errors = {
        ...errors,
        townOrCity: errorEmptyCity
      };
      isValid = false;
    } else {
      if (
        !CommonValidation.isValidLength(
          addressToValidate?.townOrCity ?? '',
          townOrCityCharLimit
        )
      ) {
        errors = {
          ...errors,
          townOrCity: t('personal-details.townorcity-error-exceeds-length', {
            townOrCityCharLimit: townOrCityCharLimit
          })
        };
        isValid = false;
      }
    }

    if (
      !CommonValidation.isValidLength(
        addressToValidate?.region ?? '',
        countyCharLimit
      )
    ) {
      errors = {
        ...errors,
        region: t('personal-details.county-error-exceeds-length', {
          countyCharLimit: countyCharLimit
        })
      };
      isValid = false;
    }

    setAddressErrors(errors);

    return isValid;
  };

  const onChangeSearchPostCode = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch({
      type: 'set-search-postcode',
      postcode: event.currentTarget.value
    });
  };

  const validatePostcode = () => {
    let isValid = true;
    const postCodeCharLimit = maxPostCodeLimit ?? 10;

    setAddressErrors({});

    if (CommonValidation.isEmpty(searchPostCode)) {
      dispatch({
        type: 'set-search-postcode-error',
        error: errorEmptySearchPostcode
      });
      isValid = false;
    } else {
      if (!CommonValidation.isValidLength(searchPostCode, postCodeCharLimit)) {
        isValid = false;
        dispatch({
          type: 'set-search-postcode-error',
          error: t('personal-details.postcode-error-exceeds-length', {
            postCodeCharLimit: postCodeCharLimit
          })
        });
      }
    }
    return isValid;
  };

  const truncate = (stringToTruncate: string, maxLength: number) => {
    return stringToTruncate.length > maxLength
      ? stringToTruncate.substr(0, maxLength - 1) + '...'
      : stringToTruncate;
  };

  const getLookupItemsAsOptions = (
    lookupItems: AddressOption[],
    includeSelect: boolean
  ) => {
    const items: SelectOpt[] = [];

    const option: SelectOpt = {
      id: '0',
      text: 'Select...',
      value: SelectInputHelper.NullValue(),
      disabled: false
    };

    if (includeSelect) {
      items.push(option);
    }

    const maxAddressLength = 35;

    lookupItems.forEach((item, index) => {
      const lookupOption: SelectOpt = {
        id: (index + 1).toString(),
        text: truncate(item.text, maxAddressLength),
        value: item.value,
        disabled: false
      };
      items.push(lookupOption);
    });

    return items;
  };

  const loadAddressesSuccess = (addresses: AddressOption[]) => {
    const addressOptions = getLookupItemsAsOptions(addresses, true);
    dispatch({
      type: 'load-addresses',
      addresses: addressOptions,
      show: true,
      error: ''
    });
  };

  const loadAddressesFailed = () => {
    dispatch({
      type: 'load-addresses',
      addresses: [],
      show: false,
      error: errorPostcodeNotFound
    });
    //this function does not reset the address textbox values in case the user has typed them and clicked the find address by mistake
  };

  const onFindClick = () => {
    setAddress({});
    const controller = new AbortController();

    const isValid = validatePostcode();

    if (isValid) {
      if (lastSearchedPostCode !== searchPostCode) {
        dispatch({
          type: 'set-finding',
          finding: true
        });

        new AddressManagerApi().loadAddresses(
          searchPostCode,
          countryCode,
          token,
          loadAddressesSuccess,
          loadAddressesFailed,
          controller.signal
        );
      }
    }

    return () => {
      controller.abort();
    };
  };

  const onShowPostcodeLookupClick = () => {
    setPostcodeLookup(true);
  };

  const setPostcodeLookup = (value: boolean) => {
    dispatch({
      type: 'set-show-postcode-look-up',
      show: value
    });
  };

  return (
    <>
      <FindAddress
        findAddressTitle={findAddressTitle}
        showAddressForm={showAddressForm}
        showAddressList={showAddressList}
        selectedAddress={selectedAddress}
        addressErrors={addressErrors}
        addressList={addressList}
        address={address}
        searchPostCode={searchPostCode}
        searchPostCodeError={searchPostCodeError}
        finding={finding}
        countries={countries}
        findAddressLabel={findAddressLabel || ''}
        showPostcodeLookUp={showPostcodeLookUp}
        onSelectAddress={onSelectAddress}
        onChangeDetail={onChangeDetail}
        onShowAddressFormClick={setAddressFormVisible}
        onFindClick={onFindClick}
        onChangeSearchPostCode={onChangeSearchPostCode}
        onCountryChange={onCountryChange}
        onShowPostcodeLookupClick={onShowPostcodeLookupClick}
        allowManualEntry={allowManualEntry}
        isMandatoryAddressline2={isMandatoryAddressline2}
      />
    </>
  );
};

export default AddressManager;
